// Modules
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Rxjs
import { Observable } from 'rxjs';
import { take, filter } from 'rxjs/operators';

// Validators
import { passwordComplexityValidator } from '@shared/validators/password-complexity.validator';
import { passwordMatchValidator } from '@shared/validators/password-match.validator';

// Constants & Helpers
import { APP_PATHS_CONSTANTS } from '@shared/constants';
import * as NavigationHelper from '@shared/helpers/navigation.helper';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as AuthActions from '@store/actions/auth.actions';
import * as GeneralActions from '@store/actions/general.actions';
// Store - Selectors
import * as AuthSelectors from '@store/selectors/auth.selectors';

@Component({
  standalone: false,
  selector: 'sbi-cot-back-password-change',
  templateUrl: './password-change.component.html',
  styleUrls: ['./password-change.component.scss'],
})
export class PasswordChangeComponent implements OnInit {
  // Inject DestroyRef for subscription cleanup
  private _destroyRef = inject(DestroyRef);

  // Public variables
  public passwordForm: FormGroup;

  // Custom error messages for fields
  public newPasswordErrors = [
    { type: 'hasUppercase', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasLowercase', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasNumber', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'hasSpecialChar', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'minLength', message: 'La contraseña no cumple con todos los requisitos' },
    { type: 'maxLength', message: 'La contraseña excede el largo máximo permitido' },
    { type: 'required', message: 'Debes ingresar una nueva contraseña' },
    // Populated dynamically from the server's validation message (see _handleServerError)
    { type: 'serverError', message: '' },
  ];

  public repeatPasswordErrors = [
    { type: 'required', message: 'Debes repetir la nueva contraseña' },
    { type: 'passwordMismatch', message: 'Las contraseñas no coinciden' },
  ];

  public currentPasswordErrors = [
    { type: 'required', message: 'Debes ingresar la contraseña actual' },
    { type: 'invalidCredentials', message: 'La contraseña actual no es correcta' },
  ];

  // Public selectors
  public changingPassword$: Observable<boolean> = this._store$.pipe(
    select(AuthSelectors.selectPasswordChangingStatus)
  );

  // Private selectors
  private _currentUser$ = this._store$.pipe(
    select(AuthSelectors.selectLoggedUser)
  );

  // Constructor
  constructor(
    private _store$: Store<AppState>,
    private _formBuilder: FormBuilder,
    private _actions$: Actions
  ) {}

  // On component init
  ngOnInit(): void {
    // Build main form
    this._buildPasswordForm();

    // Watchers
    this._handleNewPasswordChanges();
    this._handlePasswordChangeSuccess();
    this._handleLoadingState();
    this._handleInvalidCredentials();
    this._handleServerError();

    // Set current navigation
    this._store$.dispatch(
      GeneralActions.setCurrentPage({
        payload: NavigationHelper.getNotMenuNavigationItem(
          APP_PATHS_CONSTANTS.PASSWORD_CHANGE
        ),
      })
    );
  }

  // Private method to build the password form
  private _buildPasswordForm(): void {
    this.passwordForm = this._formBuilder.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, passwordComplexityValidator()]],
      newPasswordRepeat: [
        '',
        [Validators.required, passwordMatchValidator('newPassword')],
      ],
    });
  }

  // Re-trigger match validation when newPassword changes
  private _handleNewPasswordChanges(): void {
    this.passwordForm
      .get('newPassword')
      .valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.passwordForm.get('newPasswordRepeat').updateValueAndValidity();
      });
  }

  // Disable/enable form based on loading state
  private _handleLoadingState(): void {
    this.changingPassword$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(isLoading => {
        if (isLoading) {
          this.passwordForm.disable();
        } else {
          this.passwordForm.enable();
        }
      });
  }

  // Handle invalid credentials error from backend
  private _handleInvalidCredentials(): void {
    this._actions$
      .pipe(
        ofType(AuthActions.passwordChangeFailed),
        filter(action => !!action.payload?.invalidCredentials),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this.passwordForm.get('currentPassword').setErrors({ invalidCredentials: true });
        this.passwordForm.get('currentPassword').markAsTouched();
      });
  }

  // Listen for the backend validation message and show it under the new
  // password field (e.g. contains email, document, name, etc.)
  private _handleServerError(): void {
    this._actions$
      .pipe(
        ofType(AuthActions.passwordChangeFailed),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(action => {
        const fieldError = action.payload?.fieldError;
        const control = this.passwordForm.get('newPassword');
        this.newPasswordErrors.find(e => e.type === 'serverError').message =
          fieldError || '';

        if (fieldError) {
          // Exclusive: only the server's message should show
          control.setErrors({ serverError: true });
        } else if (control.hasError('serverError')) {
          control.setErrors(null);
        }
      });
  }

  // Reset form on password change success
  private _handlePasswordChangeSuccess(): void {
    this._store$
      .pipe(
        select(AuthSelectors.selectPasswordChangeSuccessStatus),
        filter(status => !!status),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this.passwordForm.reset();
        this._store$.dispatch(AuthActions.resetPasswordChangeStatuses());
      });
  }

  // Public method to perform a password change
  public onSubmit(): void {
    if (this.passwordForm.invalid) return;

    this._currentUser$.pipe(take(1)).subscribe(user => {
      this._store$.dispatch(
        AuthActions.passwordChangeRequested({
          payload: {
            email: user.email,
            newPassword: this.passwordForm.value.newPassword,
            currentPassword: this.passwordForm.value.currentPassword,
          },
        })
      );
    });
  }
}
